<template>
  <div class="choice-screen">
    <div class="choice-header">
      <v-btn icon class="choice-back" @click="$emit('back')">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <img src="@/assets/header.png" alt="APA" />
    </div>

    <div class="choice-layout">
      <section>
        <div class="choice-kicker">Submit claim</div>
        <h2>How would you like to submit?</h2>
        <p>
          Choose the path that fits what you have ready. Every option creates
          the same motor accident report for APA claims review.
        </p>
      </section>

      <div class="choice-options">
        <v-card
          outlined
          hover
          class="choice-card tw-relative"
          data-testid="choose-form"
          @click="$emit('choose-form')"
        >
          <v-avatar
            color="blue lighten-5"
            size="54"
            class="choice-avatar tw-mb-5"
          >
            <v-icon color="primary">mdi-clipboard-text-outline</v-icon>
          </v-avatar>
          <div class="choice-card-body">
            <v-chip small color="primary" class="choice-chip">
              RECOMMENDED
            </v-chip>
            <div class="choice-title">Fill in the form online</div>
            <p>Guided, about 8&ndash;10 minutes. Save and come back anytime.</p>
            <span>Best if you are starting from scratch</span>
          </div>
          <v-icon class="choice-chevron">mdi-chevron-right</v-icon>
        </v-card>

        <v-card
          outlined
          hover
          class="choice-card"
          data-testid="choose-upload"
          @click="$emit('choose-upload')"
        >
          <v-avatar
            color="amber lighten-5"
            size="54"
            class="choice-avatar tw-mb-5"
          >
            <v-icon color="secondary">mdi-upload-outline</v-icon>
          </v-avatar>
          <div class="choice-card-body">
            <div class="choice-title">
              I&rsquo;ve already filled in a paper form &mdash; let me upload it
            </div>
            <p>Photograph or scan the pages and send them to us.</p>
            <span>Best if you already have a signed form</span>
          </div>
          <v-icon class="choice-chevron">mdi-chevron-right</v-icon>
        </v-card>

        <v-card
          outlined
          :hover="aiJourneyEnabled"
          class="choice-card"
          :class="{ 'choice-card--disabled': !aiJourneyEnabled }"
          data-testid="choose-ai"
          :aria-disabled="!aiJourneyEnabled"
          @click="onAiCardClick"
          @mouseenter="!aiJourneyEnabled && (showComingSoon = true)"
          @mouseleave="showComingSoon = false"
        >
          <v-avatar
            color="teal lighten-5"
            size="54"
            class="choice-avatar tw-mb-5"
          >
            <v-icon color="teal darken-2">mdi-microphone-outline</v-icon>
          </v-avatar>
          <div class="choice-card-body">
            <div class="choice-title">Talk to our AI claims officer</div>
            <p>
              Answer a few questions out loud &mdash; we&rsquo;ll capture the
              details and draft your claim for you.
            </p>
            <span v-if="aiJourneyEnabled"
              >Best if you would rather talk than type</span
            >
            <span v-else>Temporarily unavailable</span>
          </div>
          <v-icon class="choice-chevron">mdi-chevron-right</v-icon>

          <div
            v-if="!aiJourneyEnabled && showComingSoon"
            class="choice-card-overlay"
            data-testid="choose-ai-coming-soon"
          >
            <span>Coming soon</span>
          </div>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
// Unleash flag gating the voice/AI submission path. See src/mixins/unleashMixin.js
export const AI_CLAIMS_OFFICER_FLAG = 'ASSESSMENT_CLAIM_AUDIO_JOURNEY'

export default {
  name: 'ChoiceScreen',
  emits: ['back', 'choose-form', 'choose-upload', 'choose-ai'],
  data() {
    return {
      // there's no hover on touch devices, so a tap on the disabled card
      // toggles this instead of relying on @mouseenter/@mouseleave
      showComingSoon: false,
    }
  },
  computed: {
    aiJourneyEnabled() {
      // fail open: until Unleash has fetched at least once, keep the journey
      // available rather than assuming it should be disabled
      if (this.unleashUpdated === 0) return true
      return this.isFlagEnabled(AI_CLAIMS_OFFICER_FLAG)
    },
  },
  methods: {
    onAiCardClick() {
      if (this.aiJourneyEnabled) {
        this.$emit('choose-ai')
        return
      }
      this.showComingSoon = !this.showComingSoon
    },
  },
}
</script>

<style scoped>
.choice-screen {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 48px);
  padding: 34px;
}

.choice-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 44px;
}

.choice-header img {
  width: 96px;
}

.choice-back {
  background: #f4f7fb;
}

/* centered composition: intro text on top, option cards below,
   vertically centered in the remaining viewport */
.choice-layout {
  width: 100%;
  max-width: 1120px;
  margin: auto;
  padding-bottom: 40px;
}

.choice-layout section {
  max-width: 620px;
  margin: 0 auto;
  text-align: center;
}

.choice-kicker {
  margin-bottom: 10px;
  color: #114cac;
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
}

.choice-layout h2 {
  margin: 0;
  color: #14213d;
  font-size: 38px;
  font-weight: 800;
  line-height: 1.1;
}

.choice-layout section p {
  margin: 16px 0 0;
  color: #4d4d4e;
  font-size: 16px;
  line-height: 1.6;
}

/* three paths side by side on desktop; the 900px query below stacks them */
.choice-options {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  margin-top: 36px;
}

.choice-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 280px;
  padding: 32px 28px !important;
  border-radius: 18px !important;
  text-align: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.choice-card-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: inherit;
  background: rgba(255, 255, 255, 0.92);
}

/* higher specificity than .choice-card span (and its mobile override
   further down) so this doesn't inherit the "→" arrow or that span's
   layout rules */
.choice-card .choice-card-overlay span {
  margin: 0;
  color: #114cac;
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.choice-card .choice-card-overlay span::after {
  content: none;
}

.choice-card-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
}

.choice-chip {
  margin-bottom: 10px;
}

.choice-card:hover {
  transform: translateY(-3px);
  border-color: rgba(17, 76, 172, 0.35) !important;
  box-shadow: 0 18px 42px rgba(17, 76, 172, 0.12);
}

.choice-card--disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

/* keep pointer-events enabled (unlike a plain disabled state) so hover/tap
   still reach the handlers that show the "Coming soon" overlay; the click
   handler itself no-ops the actual navigation, and the lift/shadow hover
   effect is suppressed here */
.choice-card--disabled:hover {
  transform: none;
  border-color: inherit !important;
  box-shadow: none;
}

.choice-title {
  color: #14213d;
  font-size: 21px;
  font-weight: 800;
}

.choice-card p {
  max-width: 320px;
  margin: 10px 0 18px;
  color: #4d4d4e;
  font-size: 14px;
  line-height: 1.55;
}

.choice-card span {
  margin-top: auto;
  color: #114cac;
  font-size: 12px;
  font-weight: 800;
}

.choice-card span::after {
  content: ' →';
}

/* mobile-only tap affordance */
.choice-chevron {
  display: none;
}

@media (max-width: 900px) {
  .choice-layout {
    max-width: 560px;
  }

  .choice-options {
    grid-template-columns: 1fr;
    margin-top: 24px;
  }
}

/* Mobile: compact horizontal list-row cards, softer surfaces, scaled type.
   App-shell model — pinned header, content scrolls internally only when it
   overflows the viewport */
@media (max-width: 640px) {
  .choice-screen {
    display: flex;
    flex-direction: column;
    height: 100vh;
    height: 100dvh;
    min-height: 0;
    padding: 18px 0 0;
    overflow: hidden;
  }

  .choice-header {
    flex-shrink: 0;
    margin-bottom: 16px;
    padding: 0 20px;
  }

  .choice-header img {
    width: 72px;
  }

  .choice-layout {
    flex: 1;
    gap: 18px;
    min-height: 0;
    max-width: none;
    margin: 0;
    padding: 2px 20px 24px;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  /* restore the approved left-aligned mobile composition */
  .choice-layout section {
    max-width: none;
    margin: 0;
    text-align: left;
  }

  .choice-kicker {
    margin-bottom: 6px;
    font-size: 11px;
    letter-spacing: 0.6px;
  }

  .choice-layout h2 {
    font-size: 24px;
    line-height: 1.2;
  }

  .choice-layout section p {
    margin-top: 8px;
    font-size: 14px;
    line-height: 1.55;
  }

  .choice-options {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .choice-card {
    display: flex;
    flex-direction: row;
    gap: 14px;
    align-items: flex-start;
    min-height: 0;
    padding: 16px !important;
    border-color: #e8edf5 !important;
    border-radius: 16px !important;
    box-shadow: 0 6px 18px rgba(17, 76, 172, 0.06);
    text-align: left;
  }

  .choice-card:hover {
    transform: none;
    box-shadow: 0 6px 18px rgba(17, 76, 172, 0.06);
  }

  .choice-avatar {
    width: 42px !important;
    min-width: 42px !important;
    height: 42px !important;
    margin-bottom: 0 !important;
  }

  .choice-avatar .v-icon {
    font-size: 20px !important;
  }

  .choice-card-body {
    flex: 1;
    align-items: flex-start;
    min-width: 0;
  }

  .choice-chip {
    height: 20px !important;
    margin-bottom: 6px;
    padding: 0 10px !important;
    font-size: 10px !important;
    letter-spacing: 0.4px;
  }

  .choice-title {
    font-size: 16px;
    line-height: 1.3;
  }

  .choice-card p {
    max-width: none;
    margin: 4px 0 8px;
    font-size: 13px;
    line-height: 1.5;
  }

  .choice-card span {
    margin-top: 0;
    font-size: 11.5px;
  }

  /* the chevron is the affordance on mobile; drop the desktop arrow */
  .choice-card span::after {
    content: none;
  }

  .choice-chevron {
    display: inline-flex;
    align-self: center;
    color: #9db1cc !important;
  }
}
</style>
